import { baseApi } from './baseApi';
import type { Question, QuestionDetail } from '../types';
import { applyVote, applyRecentVote, applyRecentVotes, recordRecentVote, clearRecentVote } from './voteOptimistic';

export const FEED_PAGE_SIZE = 50;

export const questionsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getQuestion: builder.query<QuestionDetail, { questionId: string }>({
      query: ({ questionId }) => `/questions/single-question/${questionId}`,
      extraOptions: { allowAnonymous: true }, // public read (isAuthGet)
      // Re-apply a just-cast vote the server data may not reflect yet (see
      // voteOptimistic's recent-vote overlay) so a refetch can't revert it.
      transformResponse: (response: QuestionDetail) => applyRecentVote(response),
      providesTags: (_result, _error, arg) => [{ type: 'Questions', id: arg.questionId }],
    }),

    // Cursor-paginated feed (infinite scroll). pageParam is "<createdAt>_<questionId>"
    // from the last item of the previous page; undefined for the first page.
    getQuestionsFeed: builder.infiniteQuery<Question[], void, string | undefined>({
      infiniteQueryOptions: {
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => {
          if (!lastPage || lastPage.length < FEED_PAGE_SIZE) return undefined; // no more pages
          const last = lastPage[lastPage.length - 1];
          return `${last.createdAt}_${last.questionId}`;
        },
      },
      query: ({ pageParam }) => {
        const params = new URLSearchParams({ limit: String(FEED_PAGE_SIZE) });
        if (pageParam) params.set('cursor', pageParam);
        return `/questions?${params.toString()}`;
      },
      extraOptions: { allowAnonymous: true }, // public read (isAuthGet)
      // Re-apply recent votes to every fetched page, so a full-feed refetch or a
      // pagination response that predates a vote can't revert the voted card.
      transformResponse: (response: Question[]) => applyRecentVotes(response),
      providesTags: ['Questions'],
    }),

    getUserQuestions: builder.query<Question[], string>({
      query: (userId) => `/questions/user/${userId}`,
      extraOptions: { allowAnonymous: true }, // public read (isAuthGet)
      providesTags: (_result, _error, userId) => [{ type: 'UserQuestions', id: userId }, 'Questions'],
      keepUnusedDataFor: 10 * 60,
    }),

    addQuestion: builder.mutation<Question, { title: string; userId?: string }>({
      query: ({ title }) => ({
        url: '/questions/create',
        method: 'POST',
        body: { title },
      }),
      invalidatesTags: (_result, _error, arg) => {
        const userId = arg.userId || localStorage.getItem('userId');
        return [
          { type: 'UserQuestions', id: userId || undefined },
          'Questions',
        ];
      },
    }),

    getQuestionsByTitle: builder.query<Question[], { title: string }>({
      query: ({ title }) => `/questions/by-title/${encodeURIComponent(title)}`,
      extraOptions: { allowAnonymous: true }, // public read (isAuthGet)
      keepUnusedDataFor: 10 * 60,
    }),

    answerQuestion: builder.mutation<unknown, { questionId: string; answer: boolean; mode?: 'toggle' | 'set' }>({
      query: ({ questionId, answer, mode }) => ({
        url: `/questions/answer/${questionId}`,
        method: 'POST',
        body: mode ? { answer, mode } : { answer },
      }),
      // On success, reconcile the caches that we do NOT patch optimistically,
      // against authoritative server state — WITHOUT refetching the feed:
      //   - `{ type: 'Questions', id }` targets only the single-question detail
      //     (it provides that id-scoped tag). The feed provides the broad
      //     'Questions' tag (no id), so an id-scoped invalidation leaves it
      //     untouched — the whole point of this change.
      //   - 'UserQuestions' / 'Bookmarks' refresh the profile and bookmark
      //     lists (keepUnusedDataFor 10m, no refetchOnMount), so returning to
      //     them after a vote shows current state instead of stale votes.
      //   - 'Soulmates': a vote changes who your opinion soulmates are, so a
      //     stale (e.g. pre-onboarding, empty) result must not survive. This is
      //     a separate tag, so it still does NOT refetch the feed.
      // Nothing is invalidated on failure (the optimistic patch is rolled back
      // in onQueryStarted instead). See PR #544 review.
      invalidatesTags: (_result, error, arg) =>
        error ? [] : [{ type: 'Questions', id: arg.questionId }, 'UserQuestions', 'Bookmarks', 'Soulmates'],
      // Patch the vote into the feed + detail caches optimistically instead of
      // invalidating the broad 'Questions' tag (which refetched the ENTIRE feed
      // on every vote — the main interactive cost on this vote-centric app). The
      // server's response shape is inconsistent (a vote doc on insert/flip, a
      // deleteOne result on toggle off), so we derive the new state from the
      // known cached state via applyVote, mirroring the backend's counter logic.
      // Rolls back on error.
      async onQueryStarted({ questionId, answer, mode }, { dispatch, queryFulfilled }) {
        // Record the vote direction so any later feed/detail refetch re-applies it
        // (transformResponse → applyRecentVote), closing the window where a
        // refetch that resolves AFTER this settles would otherwise revert the card.
        recordRecentVote(questionId, answer);

        type VoteState = { answered: boolean | null; likes: number; dislikes: number };
        const patchQuestion = <T extends VoteState>(q: T): void => {
          const next = applyVote(q, answer);
          q.answered = next.answered;
          q.likes = next.likes;
          q.dislikes = next.dislikes;
        };

        // Interactive votes (default 'toggle') get the optimistic feed/detail
        // patch for instant feedback. A 'set' replay (vote-first lock-in) is not
        // interactive, runs at load/signup, and applyVote models TOGGLE semantics
        // — so skip the optimistic patch (the overlay above + invalidation still
        // reconcile it) to avoid briefly flipping a matching vote off.
        const interactive = mode !== 'set';

        // Capture the feed question's pre- and post-vote state so we can repair
        // the optimistic patch if a concurrent fetch overwrites it WHILE the vote
        // is still in flight (the overlay covers clobbers that land after it
        // settles; this covers ones that land before). See Copilot review on #544.
        let feedPrev: VoteState | undefined;
        let feedNext: VoteState | undefined;

        const patches = interactive
          ? [
              // Every page of the infinite feed.
              dispatch(
                questionsApi.util.updateQueryData('getQuestionsFeed', undefined, (draft) => {
                  for (const page of draft.pages) {
                    const q = page.find((item) => item.questionId === questionId);
                    if (q) {
                      feedPrev = { answered: q.answered, likes: q.likes, dislikes: q.dislikes };
                      patchQuestion(q);
                      feedNext = { answered: q.answered, likes: q.likes, dislikes: q.dislikes };
                    }
                  }
                }),
              ),
              // The single-question detail view, if it's cached.
              dispatch(
                questionsApi.util.updateQueryData('getQuestion', { questionId }, (draft) => {
                  patchQuestion(draft);
                }),
              ),
            ]
          : [];

        try {
          await queryFulfilled;
          // Repair a clobbered optimistic patch. Only re-assert when the cached
          // value still equals the PRE-vote snapshot — i.e. a concurrent fetch
          // reverted it. If it already shows our vote (normal case) or a newer
          // overlapping vote changed it, leave it untouched so we don't stomp
          // that newer state.
          if (feedPrev && feedNext) {
            const prev = feedPrev;
            const next = feedNext;
            dispatch(
              questionsApi.util.updateQueryData('getQuestionsFeed', undefined, (draft) => {
                for (const page of draft.pages) {
                  const q = page.find((item) => item.questionId === questionId);
                  if (
                    q &&
                    q.answered === prev.answered &&
                    q.likes === prev.likes &&
                    q.dislikes === prev.dislikes
                  ) {
                    q.answered = next.answered;
                    q.likes = next.likes;
                    q.dislikes = next.dislikes;
                  }
                }
              }),
            );
          }
        } catch {
          // Vote failed: drop the overlay (so a later refetch doesn't re-assert a
          // vote that never persisted) and roll back the optimistic patches.
          clearRecentVote(questionId);
          patches.forEach((p) => p.undo());
        }
      },
    }),
  }),
});

export const {
  useGetQuestionQuery,
  useGetQuestionsFeedInfiniteQuery,
  useGetUserQuestionsQuery,
  useAddQuestionMutation,
  useGetQuestionsByTitleQuery,
  useLazyGetQuestionsByTitleQuery,
  useAnswerQuestionMutation,
} = questionsApi;
